import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Info, Loader2, ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";
import { SearchCard } from "../components/site/SearchCard";
import { TripCard } from "../components/site/TripCard";
import { SeatDialog, CheckoutDialog, LEG_LABEL, legTotal } from "../components/site/BookingDialog";
import { WaitlistDialog } from "../components/site/WaitlistDialog";
import { DayStrip } from "../components/site/DayStrip";
import { MaskLines, Eyebrow } from "../components/site/Motion";
import { api, apiError } from "../lib/api";
import { useCities } from "../lib/useCities";
import { formatARS } from "../lib/site";

const Leg = ({ leg, o, d, f, p, refresh, onSelect, onWaitlist, children }) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true); setError("");
    api.get("/trips/search", { params: { origin: o, destination: d, date: f, passengers: p } })
      .then((r) => { if (alive) setTrips(Array.isArray(r.data?.trips) ? r.data.trips : []); })
      .catch((e) => { if (alive) setError(apiError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [o, d, f, p, refresh]);

  const title = LEG_LABEL[leg];
  return (
    <div className="mt-14 scroll-mt-24" data-testid={`results-leg-${leg}`}>
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <p className="font-display text-3xl md:text-4xl">{title}: {o} → {d}</p>
        <p className="font-mono text-sm text-[var(--muted-ink)]">{new Date(`${f}T00:00`).toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" })}</p>
      </div>
      {children}
      {loading && <div className="flex items-center gap-2 text-[var(--muted-ink)]"><Loader2 className="h-5 w-5 animate-spin" /> Buscando salidas…</div>}
      {error && <p data-testid="results-error" className="text-red-700">{error}</p>}
      <div className="space-y-4">
        {trips.map((t, i) => <TripCard key={t.key ?? t.id} trip={t} index={leg === "vuelta" ? i + 10 : i} onSelect={(trip) => onSelect(leg, trip)} onWaitlist={onWaitlist} />)}
        {!loading && !error && trips.length === 0 && <p data-testid={leg === "ida" ? "results-empty" : "results-empty-vuelta"} className="rounded-2xl bg-white p-6 text-[var(--muted-ink)]">No hay servicios a la venta para esa fecha y tramo. Elegí uno de los días con salida.</p>}
      </div>
    </div>
  );
};

const Pick = ({ label, value, onChange, options, testid, type }) => (
  <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-[var(--muted-ink)]">
    {label}
    {type === "date"
      ? <input type="date" data-testid={testid} value={value} min={options} onChange={(e) => e.target.value && onChange(e.target.value)} className="h-11 rounded-xl border border-black/10 bg-white px-3 text-sm font-semibold text-[var(--ink)]" />
      : <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="h-11 rounded-xl border border-black/10 bg-white px-3 text-sm font-semibold text-[var(--ink)]">{options.map((c) => <option key={c} value={c}>{c}</option>)}</select>}
  </label>
);

const ReturnRoute = ({ o, d, f, min, onChange }) => {
  const cities = useCities();
  return (
    <div data-testid="return-route" className="mb-5 rounded-3xl bg-white p-4">
      <p className="mb-3 text-sm font-semibold">¿Volvés desde o hacia otra parada? Cambiá el tramo de regreso:</p>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Pick label="Regreso desde" value={o} options={cities.filter((c) => c !== d)} onChange={(v) => onChange({ vo: v })} testid="return-origin" />
        <Pick label="Regreso hasta" value={d} options={cities.filter((c) => c !== o)} onChange={(v) => onChange({ vd: v })} testid="return-destination" />
        <Pick label="Fecha de regreso" type="date" value={f} options={min} onChange={(v) => onChange({ r: v })} testid="return-date" />
      </div>
    </div>
  );
};

const CartLeg = ({ l, onEdit, onRemove }) => (
  <div data-testid={`cart-leg-${l.leg}`} className="flex min-w-0 items-center gap-2 rounded-2xl bg-white/10 px-3 py-2 text-sm">
    <button type="button" onClick={onEdit} data-testid={`cart-edit-${l.leg}`} className="min-w-0 truncate text-left">
      <b className="text-[var(--lime)]">{LEG_LABEL[l.leg]}</b> {l.trip.origin} → {l.trip.destination} · butacas {l.seats.join(", ")} · {formatARS(legTotal(l))}
    </button>
    <button type="button" onClick={onRemove} data-testid={`cart-remove-${l.leg}`} aria-label={`Quitar ${LEG_LABEL[l.leg]}`} className="shrink-0 rounded-full p-1 hover:bg-white/15"><X className="h-4 w-4" /></button>
  </div>
);

const CartBar = ({ legs, round, onEdit, onRemove, onCheckout }) => (legs.length ? (
  <div data-testid="cart-bar" className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[var(--ink)] px-4 py-3 text-white shadow-[0_-20px_40px_-20px_rgba(0,0,0,.5)] md:px-10">
    <div className="mx-auto flex max-w-[1400px] flex-col gap-2 md:flex-row md:items-center">
      <div className="flex min-w-0 flex-1 flex-col gap-2 md:flex-row">
        {legs.map((l) => <CartLeg key={l.leg} l={l} onEdit={() => onEdit(l)} onRemove={() => onRemove(l.leg)} />)}
        {round && legs.length === 1 && legs[0].leg === "ida" && <p className="self-center text-xs text-white/75">Elegí tu vuelta más abajo o seguí solo con la ida.</p>}
      </div>
      <button type="button" onClick={onCheckout} data-testid="cart-checkout-button" className="flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--lime)] px-6 font-bold text-[var(--ink)] mr-16 md:mr-20">
        <ShoppingBag className="h-4 w-4" /> Continuar · {formatARS(legs.reduce((a, l) => a + legTotal(l), 0))}
      </button>
    </div>
  </div>
) : null);

export default function Pasajes() {
  const [sp, setSp] = useSearchParams();
  const q = useMemo(() => Object.fromEntries(sp.entries()), [sp]);
  const [pick, setPick] = useState(null);
  const [cart, setCart] = useState({});
  const [checkout, setCheckout] = useState(false);
  const [waiting, setWaiting] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const has = q.o && q.d && q.f;
  const vo = q.vo || q.d;
  const vd = q.vd || q.o;
  const idaKey = `${q.o}|${q.d}|${q.f}`;
  const vueltaKey = `${vo}|${vd}|${q.r}`;
  useEffect(() => { setCart((c) => ({ ...c, ida: undefined })); }, [idaKey]);
  useEffect(() => { setCart((c) => ({ ...c, vuelta: undefined })); }, [vueltaKey]);
  const legs = ["ida", "vuelta"].map((k) => cart[k]).filter(Boolean);

  const update = (patch) => {
    const n = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    if (n.get("r") && n.get("r") < n.get("f")) n.set("r", n.get("f"));
    setSp(n, { replace: true });
  };
  const confirm = (l) => {
    const next = { ...cart, [l.leg]: l };
    setCart(next);
    setPick(null);
    if (l.leg === "ida" && q.r && !next.vuelta) {
      toast.success("Ida lista. Ahora elegí tu vuelta.");
      setTimeout(() => document.querySelector('[data-testid="results-leg-vuelta"]')?.scrollIntoView({ behavior: "smooth" }), 250);
      return;
    }
    setCheckout(true);
  };

  return (
    <section data-testid="pasajes-page" className="min-h-[70vh] bg-[var(--paper)] pb-40">
      <div className="mx-auto max-w-[1400px] px-5 pt-12 md:px-10 md:pt-16">
        <Eyebrow>Pasajes</Eyebrow>
        <h1 className="mt-5 font-display text-[2.6rem] sm:text-5xl lg:text-6xl"><MaskLines lines={has ? ["Elegí tu salida."] : ["¿A dónde", "viajamos?"]} /></h1>
        {has && <div className="mt-8"><DayStrip o={q.o} d={q.d} f={q.f} onPick={(f) => update({ f })} testid="ida-days" /></div>}
        <div className="mt-6"><SearchCard key={sp.toString()} initial={q} compact /></div>
        {has && (
          <div data-testid="sample-data-notice" className="mt-6 flex items-start gap-3 rounded-2xl bg-[var(--lime)]/25 px-5 py-4 text-sm">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--forest)]" />
            <span>Para reservar o comprar necesitás una cuenta. Podés elegir varias butacas y combinar categorías; en ida y vuelta elegís las butacas de cada tramo y confirmás todo junto. Si reservás, pagás en cualquier boletería hasta 2 horas antes de la salida; si no, la reserva vence.</span>
          </div>
        )}
        {has && <Leg leg="ida" o={q.o} d={q.d} f={q.f} p={q.p || 1} refresh={refresh} onSelect={(leg, trip) => setPick({ leg, trip, initial: cart.ida?.trip.id === trip.id ? cart.ida.seats : [] })} onWaitlist={setWaiting} />}
        {has && q.r && (
          <Leg leg="vuelta" o={vo} d={vd} f={q.r} p={q.p || 1} refresh={refresh} onSelect={(leg, trip) => setPick({ leg, trip, initial: cart.vuelta?.trip.id === trip.id ? cart.vuelta.seats : [] })} onWaitlist={setWaiting}>
            <ReturnRoute o={vo} d={vd} f={q.r} min={q.f} onChange={update} />
            <div className="mb-5"><DayStrip o={vo} d={vd} f={q.r} onPick={(r) => update({ r })} testid="vuelta-days" /></div>
          </Leg>
        )}
      </div>
      <CartBar legs={legs} round={Boolean(q.r)} onEdit={(l) => setPick({ leg: l.leg, trip: l.trip, initial: l.seats })} onRemove={(k) => setCart({ ...cart, [k]: undefined })} onCheckout={() => setCheckout(true)} />
      <SeatDialog pick={pick} onClose={() => setPick(null)} onConfirm={confirm} />
      <CheckoutDialog legs={legs} open={checkout} onClose={() => setCheckout(false)} onBooked={() => { setCart({}); setRefresh((n) => n + 1); }} />
      <WaitlistDialog trip={waiting} onClose={() => setWaiting(null)} />
    </section>
  );
}
